/**
 * VOICE1-B, SLICE 0 (the 135th, 2026-10-11) - THE PAGE'S OWN PLAYER.
 *
 * His words: "the voices are bad ... The only US voice ... sounds like it's the same iPhone voice" and "yes lets build
 * voice plumbing". A better voice cannot come from the phone; it comes as a CLIP from our server (`POST /api/voice/speak`,
 * one sentence a request) and the page plays it. This is that player - and nothing else: it knows no React and no server.
 *
 * ⭐⭐ THE RULE IT IS BUILT ON WAS MEASURED, NOT ASSUMED (`docs/planning/2026-10-11-voice-play-probe/`, the iPhone simulator
 *    on iOS 26.0.1, a Safari tab AND the home-screen app - the same in both):
 *      - a clip asked for with no tap at all: REFUSED (`NotAllowedError`).
 *      - a clip played INSIDE a tap: plays. After that, NEW clips play 3, 6, 7 and 30 seconds later with no tap.
 *      - a clip paused mid-way, then a new one: the new one plays.
 *      - 🚨 an `ended` listener left on a reused element fires for the NEXT clip.
 *    So `unlock` plays one quiet clip inside a tap, there is ONE element, and every play is numbered - an event of an
 *    earlier play is ignored.
 * ⛔ NEVER A DEAD BUTTON, NEVER TWO VOICES AT ONCE. A sentence whose clip does not come (no voice set up, a fault, too
 *    slow, the phone refusing to play it) is said by the phone's own voice IN ITS PLACE, in order - and the rest of what
 *    is waiting stays in the phone's voice, so one answer does not change voice sentence by sentence. Once everything
 *    waiting has been said, the next sentence tries the server's voice again.
 * ⚠️ NOT measured, and only his phone can: whether this sound is heard at full loudness right after the microphone
 *    closes, and whether the silent switch mutes it.
 */

/** A clip not here this long after it was asked for is given up. The phone's own voice starts in well under a second. */
export const CLIP_MUST_ARRIVE_WITHIN_MS = 4000;
/** A clip asked to play that has not STARTED by now is given up. Once started it may take as long as it likes. */
export const CLIP_MUST_START_WITHIN_MS = 2000;

/** What the player is told about one sentence: when its sound began, and when it is over. */
export interface Spoken { start: () => void; end: () => void }

export interface PageVoiceDeps {
  /** One sentence as a clip - or null when the server has none for it. Must give up when `signal` says so. */
  fetchClip: (text: string, signal: AbortSignal) => Promise<Blob | null>;
  /** The phone's own voice says this sentence instead, and tells when it began and when it is over. */
  viaDevice: (text: string, on: Spoken) => void;
  /** The one audio element. Asked for once, the first time it is needed. */
  audio: () => HTMLAudioElement;
  toUrl: (clip: Blob) => string;
  freeUrl: (url: string) => void;
  /** The address of a clip nobody can hear, played inside a tap so that later clips may play by themselves. */
  quietClip: string;
}

export interface PageVoice {
  /** ⛔ CALL INSIDE A TAP. Does nothing while a sentence is being said or waiting. */
  unlock: () => void;
  /** One sentence: its clip is fetched NOW and played when everything asked for before it has been said. */
  say: (text: string, on: Spoken) => void;
  /** Cuts off what is being said and forgets what was waiting. Nothing more is told about any of it. */
  stop: () => void;
  /** Right now, is anything being said or waiting to be? */
  busy: () => boolean;
}

interface Waiting { text: string; on: Spoken; clip: Promise<Blob | null> | null; giveUp: AbortController | null }

export const createPageVoice = (deps: PageVoiceDeps): PageVoice => {
  let element: HTMLAudioElement | null = null;
  const audio = (): HTMLAudioElement => (element ??= deps.audio());

  let queue: Waiting[] = [];
  /** Bumped by every stop: whatever an earlier round was waiting on reports back later and must change nothing. */
  let round = 0;
  /** Bumped by every play: an event of an earlier clip must not be counted for this one (measured). */
  let playNo = 0;
  let pumping = false;
  /** A clip did not come: what is waiting is said by the phone's own voice. Cleared when the queue has emptied. */
  let phoneOnly = false;

  const fetched = (text: string): Pick<Waiting, 'clip' | 'giveUp'> => {
    const giveUp = new AbortController();
    const clip = new Promise<Blob | null>((resolve) => {
      const tooSlow = setTimeout(() => { giveUp.abort(); resolve(null); }, CLIP_MUST_ARRIVE_WITHIN_MS);
      deps.fetchClip(text, giveUp.signal).then(
        (blob) => { clearTimeout(tooSlow); resolve(blob); },
        () => { clearTimeout(tooSlow); resolve(null); },
      );
    });
    return { clip, giveUp };
  };

  /** Plays one clip. Answers 'played' when it is over, or 'failed' when it never started. */
  const play = (blob: Blob, on: Spoken, mine: number): Promise<'played' | 'failed'> => new Promise((resolve) => {
    const el = audio();
    const url = deps.toUrl(blob);
    playNo += 1;
    const no = playNo;
    let started = false;
    let over = false;
    const current = (): boolean => no === playNo && mine === round && !over;
    const finish = (how: 'played' | 'failed'): void => {
      if (!current()) return;
      over = true;
      clearTimeout(neverStarted);
      deps.freeUrl(url);
      resolve(how);
    };
    const neverStarted = setTimeout(() => {
      if (!current()) return;
      el.pause();
      finish('failed');
    }, CLIP_MUST_START_WITHIN_MS);
    el.onplaying = () => {
      if (!current() || started) return;
      started = true;
      clearTimeout(neverStarted);
      on.start();
    };
    el.onended = () => finish(started ? 'played' : 'failed');
    // Broken before it began: the phone says the sentence. Broken part-way: it is not said a second time.
    el.onerror = () => finish(started ? 'played' : 'failed');
    el.src = url;
    try {
      el.play().catch(() => finish('failed'));
    } catch {
      finish('failed');
    }
  });

  const viaDevice = (item: Waiting): Promise<void> => new Promise((resolve) => {
    deps.viaDevice(item.text, { start: item.on.start, end: resolve });
  });

  const pump = async (): Promise<void> => {
    if (pumping) return;
    pumping = true;
    const mine = round;
    while (queue.length > 0) {
      const item = queue[0];
      const blob = phoneOnly || !item.clip ? null : await item.clip;
      if (mine !== round) return;
      let said = false;
      if (blob) said = (await play(blob, item.on, mine)) === 'played';
      if (mine !== round) return;
      if (!said) {
        phoneOnly = true;
        await viaDevice(item);
        if (mine !== round) return;
      }
      queue.shift();
      item.on.end();
    }
    pumping = false;
    phoneOnly = false;
  };

  const say = (text: string, on: Spoken): void => {
    queue.push({ text, on, ...(phoneOnly ? { clip: null, giveUp: null } : fetched(text)) });
    void pump();
  };

  const stop = (): void => {
    round += 1;
    playNo += 1;
    for (const item of queue) item.giveUp?.abort();
    queue = [];
    pumping = false;
    phoneOnly = false;
    if (element) {
      element.onplaying = null;
      element.onended = null;
      element.onerror = null;
      element.pause();
    }
  };

  const unlock = (): void => {
    if (queue.length > 0) return;
    const el = audio();
    el.src = deps.quietClip;
    try {
      // A phone that refuses is not an error here: the first sentence then falls to the phone's own voice.
      el.play().catch(() => undefined);
    } catch { /* the same */ }
  };

  return { unlock, say, stop, busy: () => queue.length > 0 };
};

/**
 * The clip `unlock` plays: a tenth of a second of a tone at an amplitude of 2 in 32,767 - the probe's own clip. It has
 * real sound in it as far as the phone's rules go (it is not muted), and nothing anybody can hear.
 */
export const quietClip = (): Blob => {
  const rate = 8000;
  const samples = rate / 10;
  const view = new DataView(new ArrayBuffer(44 + samples * 2));
  const word = (at: number, text: string): void => { for (let i = 0; i < text.length; i += 1) view.setUint8(at + i, text.charCodeAt(i)); };
  word(0, 'RIFF');
  view.setUint32(4, view.byteLength - 8, true);
  word(8, 'WAVE');
  word(12, 'fmt ');
  view.setUint32(16, 16, true);       // the format block's own length
  view.setUint16(20, 1, true);        // plain PCM
  view.setUint16(22, 1, true);        // one channel
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true); // bytes a second
  view.setUint16(32, 2, true);        // bytes a sample
  view.setUint16(34, 16, true);       // bits a sample
  word(36, 'data');
  view.setUint32(40, samples * 2, true);
  for (let i = 0; i < samples; i += 1) view.setInt16(44 + i * 2, Math.round(Math.sin((2 * Math.PI * 440 * i) / rate) * 2), true);
  return new Blob([view.buffer], { type: 'audio/wav' });
};
